import { formatDate } from "../../lib/format-date";
import { useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProviderScreenings } from "../provider-screenings/provider-screenings";
import {
  assessmentOutcome,
  CHECK_OUTCOME_PRESENTATION,
  type CheckOutcome,
} from "./check-outcome";
import { OtherCheckResults } from "./other-check-results";
import { RegistryCheckResults } from "./registry-check-results";
import { SourceRowHeader } from "./source-row-header";
import { checkDetailLimitations } from "./run-report-dialog/item-status";
import type { ReportItem, RunReport, RunScope } from "./types";

type SourceCheck = RunReport["checks"][number];
type Assessment = SourceCheck["assessment"];
type AssessmentItem = Assessment["items"][number];
/** Collapsed-row facts per family. The NPPES row shows its NPI, registered
 * name and status; findings live in the status pill and the details. Medicare
 * is derived instead; see {@link medicareRowDetails}. */
const OVERVIEW_LABELS = {
  identity: new Set(["Registered name", "Registry status"]),
  dea: new Set([
    "Registered name",
    "Registry status",
    "Expires",
    "Registration state",
  ]),
};

/** Families whose rows show only an identifier and bare values. */
const COMPACT_FAMILIES = new Set<string>([
  "identity",
  "medicare-participation",
]);
const REGISTRY_STATUS_LABEL = "Registry status";
/**
 * Exclusion screenings whose rows show only who was searched and the dataset
 * release searched. The dataset's publish date sits at a different path in
 * each source's saved facts.
 */
const SCREENING_ROWS: Record<
  string,
  { prefix: string; label: string; snapshot: "dataset" | "samSnapshot" }
> = {
  "oig-leie": {
    prefix: "OIG data",
    label: "LEIE published",
    snapshot: "dataset",
  },
  "sam-exclusions": {
    prefix: "SAM data",
    label: "SAM extract published",
    snapshot: "samSnapshot",
  },
};
/** Medicare opt-out detail labels the API assigns; the first affidavit is
 * the one a collapsed row summarizes. */
const MEDICARE_DETAIL = {
  ReportingPeriod: "CMS reporting period",
  OptOutEffective: "Affidavit 1 · Effective",
  OptOutEnd: "Affidavit 1 · End",
} as const;
const ISO_DATE_PATTERN = /\d{4}-\d{2}-\d{2}/g;
const SUMMARY_FACT = "min-w-0 border-l pl-3";
const IN_PROGRESS_STYLE = "border-blue-200 bg-blue-50 text-blue-800";

type StatusSource = Pick<Assessment, "status" | "label"> & {
  items?: unknown[];
};

/** The pill for an item or check outcome; null is a source still running. */
export function OutcomePill({
  outcome,
  inProgressLabel = "In progress",
}: {
  outcome: CheckOutcome | null;
  inProgressLabel?: string;
}) {
  const presentation = outcome ? CHECK_OUTCOME_PRESENTATION[outcome] : null;
  const Icon = presentation?.icon ?? Loader2;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
        presentation?.style ?? IN_PROGRESS_STYLE,
      )}
    >
      <Icon
        className={cn("size-3.5", !presentation && "motion-safe:animate-spin")}
        aria-hidden
      />
      {presentation?.label ?? inProgressLabel}
    </span>
  );
}

export function AssessmentBadge({ assessment }: { assessment: StatusSource }) {
  return (
    <OutcomePill
      outcome={assessmentOutcome(assessment)}
      inProgressLabel={assessment.label}
    />
  );
}

function ResultOverview({
  item,
  showBadge,
  sourceName,
  details,
  identifier,
}: {
  item: AssessmentItem;
  showBadge: boolean;
  sourceName: string;
  /** The facts this row shows, from {@link rowDetails}. */
  details: { label: string; value: string }[];
  /** Set for compact rows: the row shows only this identifier and bare
   * values. Findings, caveats and New York's separate licensure and OPMC
   * results are in the status pill and the details. Null falls back to the
   * server title. */
  identifier?: string | null;
}) {
  const compact = identifier !== undefined;
  // An identifier always shows; a server title only when it adds to the
  // source name above it.
  const showTitle = Boolean(identifier) || item.title !== sourceName;
  return (
    <div className="space-y-1">
      {/* Each fact has a left divider. The row is shifted left by one
          divider plus its padding and clipped, so the first fact on every
          wrapped line starts flush without a stray divider. */}
      <div className="overflow-hidden">
        <div className="-ml-[13px] flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
          {showTitle && (
            <h5 className={cn(SUMMARY_FACT, "font-medium")}>
              {identifier ?? item.title}
            </h5>
          )}
          {showBadge && (
            <span className={SUMMARY_FACT}>
              <AssessmentBadge assessment={item} />
            </span>
          )}
          {details.length > 0 && (
            <dl className="contents">
              {details.map((detail, index) => (
                <div
                  key={`${detail.label}-${index}`}
                  className={cn(SUMMARY_FACT, "flex flex-wrap gap-x-1")}
                >
                  {/* Compact values read as bare facts beside the
                      identifier; the label stays for assistive technology. */}
                  <dt className={compact ? "sr-only" : "text-muted-foreground"}>
                    {detail.label}:
                  </dt>
                  <dd className="break-words">{detail.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
      {!compact && (
        <p className="text-xs leading-5 text-muted-foreground">
          {item.description}
        </p>
      )}
      {!compact && item.limitations.length > 0 && (
        <p className="text-xs text-amber-900">
          Coverage limitation · see details
        </p>
      )}
    </div>
  );
}

/** A check's result as an in-place expandable row. Used inside the state
 * Medicaid group, one row per state. */
export function SourceResultRow({
  check,
  runId,
  scope,
  resultKey,
  issues,
}: {
  check: SourceCheck;
  runId: string;
  scope: RunScope;
  /** Scopes the row's provider screenings to one result of a shared check,
   * such as a single state within the state Medicaid exclusion check. */
  resultKey?: string;
  issues?: ReportItem["issues"];
}) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const assessment = check.assessment;
  const description = assessment.description;
  const visibleItems = assessment.items;
  const detailLimitations = checkDetailLimitations(check);
  const compactRows = isCompactRow(check);
  const summaryFacts = (
    <>
      {/* Compact rows explain themselves in the details; a check with no
          result (failed or unsupported) still says why here. */}
      {!(compactRows && visibleItems.length > 0) &&
        (visibleItems.length !== 1 ||
          description !== visibleItems[0]?.description) && (
          <p className="text-xs leading-5 text-muted-foreground">
            {description}
          </p>
        )}
      <div className="divide-y space-y-2 [&>div+div]:pt-2">
        {visibleItems.map((item) => (
          <ResultOverview
            key={item.resultKey}
            item={item}
            showBadge={visibleItems.length > 1}
            sourceName={check.authorityName}
            details={rowDetails(check, item)}
            identifier={
              compactRows ? rowIdentifier(check, item.resultKey) : undefined
            }
          />
        ))}
      </div>
    </>
  );
  return (
    <article
      aria-label={`${check.authorityName} result`}
      className="overflow-hidden"
    >
      <SourceRowHeader
        title={check.authorityName}
        badge={<AssessmentBadge assessment={assessment} />}
        summary={summaryFacts}
        label={`${open ? "Hide" : "View"} details for ${check.authorityName}`}
        onClick={() => setOpen(!open)}
        disclosure={{ expanded: open, controls: detailsId }}
      />
      {open && (
        <div id={detailsId} className="space-y-3 p-3 sm:p-5">
          <CheckLimitations
            check={check}
            detailLimitations={detailLimitations}
          />
          <SourceDetails
            check={check}
            runId={runId}
            scope={scope}
            resultKey={resultKey}
            issues={issues}
          />
        </div>
      )}
    </article>
  );
}

/** State license caveats render inside the card, beside the board record or
 * screening result each one qualifies; other-family checks list them here. */
export function CheckLimitations({
  check,
  detailLimitations,
}: {
  check: SourceCheck;
  detailLimitations: {
    resultKey: string;
    title: string;
    limitations: string[];
  }[];
}) {
  return detailLimitations
    .filter((item) => check.family === "other" && item.limitations.length > 0)
    .map((item) => (
      <div
        key={item.resultKey}
        className="space-y-1 text-xs leading-5 text-amber-900"
      >
        <p className="font-medium">{item.title} · limitations</p>
        <ul className="list-disc space-y-1 pl-4">
          {item.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </div>
    ));
}

/** The row already names the source, so a result is identified by its number
 * alone rather than the server's "NPI <number>" title. */
function rowIdentifier(check: SourceCheck, resultKey: string) {
  const facts = resultFacts(check, resultKey);
  // A screening is identified by who was searched.
  if (check.sourceId in SCREENING_ROWS) {
    const names = Array.isArray(facts?.queriedNames)
      ? facts.queriedNames.filter(
          (name): name is string => typeof name === "string",
        )
      : [];
    return names.length ? names.join(", ") : null;
  }
  const number = facts?.requestedNpi;
  return typeof number === "string" && number ? number : null;
}

/**
 * The facts a collapsed row shows. Compact rows keep only the values that
 * answer the check; the dialog lists everything.
 */
function rowDetails(
  check: SourceCheck,
  item: AssessmentItem,
): AssessmentItem["details"] {
  const family = check.family;
  if (family === "medicare-participation") {
    return medicareRowDetails(item);
  }
  const screening = SCREENING_ROWS[check.sourceId];
  if (screening) {
    return screeningRowDetails({
      facts: resultFacts(check, item.resultKey),
      screening,
    });
  }
  const labels = OVERVIEW_LABELS[family as keyof typeof OVERVIEW_LABELS];
  const details = labels
    ? item.details.filter((detail) => labels.has(detail.label))
    : item.details;
  if (family !== "identity") {
    return details;
  }
  // NPPES reports statuses in lower case ("active"); the row starts them
  // with a capital so they read as a status rather than a code.
  return details.map((detail) =>
    detail.label === REGISTRY_STATUS_LABEL
      ? {
          ...detail,
          value: detail.value.charAt(0).toUpperCase() + detail.value.slice(1),
        }
      : detail,
  );
}

/**
 * A Medicare row answers one question: did CMS list an opt-out? It shows the
 * first affidavit's period when there is one, otherwise the CMS data period
 * that was searched. A bare date range would be ambiguous, so each keeps a
 * one-word prefix instead of its label.
 */
function medicareRowDetails(item: AssessmentItem) {
  const value = (label: string) =>
    item.details.find((detail) => detail.label === label)?.value;
  const effective = value(MEDICARE_DETAIL.OptOutEffective);
  if (effective) {
    const end = value(MEDICARE_DETAIL.OptOutEnd);
    return [
      {
        label: "Opt-out period",
        value: `Opt-out ${formatDates(effective)}${end ? ` – ${formatDates(end)}` : ""}`,
      },
    ];
  }
  const period = value(MEDICARE_DETAIL.ReportingPeriod);
  return period
    ? [
        {
          label: MEDICARE_DETAIL.ReportingPeriod,
          value: `CMS data ${formatDates(period)}`,
        },
      ]
    : [];
}

/**
 * A screening row shows only the dataset release searched. Potential matches
 * are findings: the status pill flags them and the reviewer sees them in the
 * details, so neither the row nor a reviewed row repeats them.
 */
function screeningRowDetails({
  facts,
  screening,
}: {
  facts: Record<string, unknown> | undefined;
  screening: (typeof SCREENING_ROWS)[string];
}): AssessmentItem["details"] {
  const snapshot = facts?.[screening.snapshot] as
    | { publishedAt?: unknown }
    | undefined;
  if (typeof snapshot?.publishedAt !== "string") {
    return [];
  }
  return [
    {
      label: screening.label,
      value: `${screening.prefix} ${formatDate(snapshot.publishedAt)}`,
    },
  ];
}

function resultFacts(check: SourceCheck, resultKey: string) {
  return check.results?.find((result) => result.resultKey === resultKey)
    ?.facts as Record<string, unknown> | undefined;
}

function isCompactRow(check: SourceCheck) {
  return COMPACT_FAMILIES.has(check.family) || check.sourceId in SCREENING_ROWS;
}

/** CMS values carry ISO dates, alone or as a range. */
function formatDates(value: string) {
  return value.replace(ISO_DATE_PATTERN, (date) => formatDate(date));
}

export function SourceDetails({
  check,
  runId,
  scope,
  resultKey,
  issues,
}: {
  check: SourceCheck;
  runId: string;
  scope: RunScope;
  resultKey?: string;
  issues?: ReportItem["issues"];
}) {
  if (
    check.family === "identity" ||
    check.family === "dea" ||
    check.family === "medicare-participation"
  ) {
    return (
      <RegistryCheckResults
        check={check}
        runId={runId}
        scope={scope}
        resultKey={resultKey}
      />
    );
  }
  if (check.family === "exclusion" || check.family === "sanctions") {
    return (
      <ProviderScreenings
        {...scope}
        runId={runId}
        checkId={check.id}
        resultKey={resultKey}
        issues={issues}
        open
      />
    );
  }
  return <OtherCheckResults check={check} runId={runId} scope={scope} />;
}
